import {
    trackPageview,
    trackEcommerce,
    trackingConstants,
    eventNameConstants,
    getSessionPageCount,
    getCustomDimensions,
} from 'dibs-tracking';
import serverVars from 'server-vars';
import { useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useBuyerUrgency } from 'dibs-buyer-urgency/exports/useBuyerUrgency';
import { getPriceStatus } from '../PdpSharedPricingGuidance/pricingGuidanceTracking';

// types
import { usePdpSharedTracking_item$key as Item } from './__generated__/usePdpSharedTracking_item.graphql';
import { usePdpSharedTracking_viewer$key as Viewer } from './__generated__/usePdpSharedTracking_viewer.graphql';

const ADDITIONAL_TRACKING_DEFAULTS = {
    isCondensed: 'N',
    personalization: 'false',
};

const CUSTOM_DIMENSIONS_TO_TRACK = [
    'onSale',
    'newListing',
    'dealerName',
    'dealerID',
    'dealerLocation',
    'itemPostingLocation',
    'purchaseStatus',
    'askingPrice',
    'priceType',
];

const viewerFragment = graphql`
    fragment usePdpSharedTracking_viewer on Viewer {
        ...useBuyerUrgency_viewer @arguments(itemId: $itemId, userId: $userId)
    }
`;

const itemFragment = graphql`
    fragment usePdpSharedTracking_item on Item {
        ... @include(if: $afterDisplay) {
            browseUrl
            categoryCode
            contemporaryTrackingString
            pdpTrackingVpvParams(userId: $userId, userCountryCode: $userCountryCode)
            viewInRoomPhotos {
                __typename
            }
            pricingGuidance(
                isTrade: $isTrade
                buyerId: $userId
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            ) {
                priceRange
            }
        }
    }
`;

export const usePdpSharedTracking = ({
    item: itemRef,
    viewer: viewerRef,
    additionalTrackingParams = {},
}: {
    item: Item;
    viewer: Viewer;
    additionalTrackingParams?: Record<string, string>;
}): void => {
    const hasFiredTracking = useRef<boolean>(false);
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { showBuyerUrgencyBanner: buyerUrgency } = useBuyerUrgency({ viewer });

    const { pdpTrackingVpvParams } = item;
    const { url, ...additional } = pdpTrackingVpvParams || {};

    useEffect(() => {
        if (url && !hasFiredTracking.current) {
            const {
                browseUrl,
                categoryCode,
                pricingGuidance,
                viewInRoomPhotos,
                contemporaryTrackingString,
            } = item;
            const pageCountViewed = getSessionPageCount();
            const hasViewInroom = !!viewInRoomPhotos?.length;
            const soldPrice = getPriceStatus(pricingGuidance?.priceRange || null);
            trackPageview({
                url,
                additional: {
                    soldPrice,
                    buyerUrgency,
                    hasViewInroom,
                    pageCountViewed,
                    opt_pageURL: url,
                    ...additional,
                    ...{
                        ...ADDITIONAL_TRACKING_DEFAULTS,
                        ...additionalTrackingParams,
                    },
                },
            });
            trackEcommerce({
                type: trackingConstants.ECOM_PRODUCT_DETAIL,
                eventName: eventNameConstants.EVENT_VIEW_ITEM,
                products: [
                    {
                        category: browseUrl,
                        id: additional.itemId,
                        categoryCode: categoryCode,
                        brand: additional.dealerId,
                        price: additional.askingPrice,
                        dimension12: additional.dealerId,
                        dimension83: contemporaryTrackingString || additional.contemporary,
                        name: serverVars.get('locale') === 'en-US' ? additional.itemName : 'n/a',
                        ...getCustomDimensions(additional, CUSTOM_DIMENSIONS_TO_TRACK),
                    },
                ],
            });
            hasFiredTracking.current = true;
        }
    }, [item, additional, url, buyerUrgency, additionalTrackingParams]);
};
